import { ActionIcon, Button, Center, Group, MultiSelect, rem, SegmentedControl, Select, Text } from "@mantine/core";
import { Level, Mode, PlayerColor, Position, Settings, User } from "./models";
import { useViewportSize } from "@mantine/hooks";
import { IconLock, IconPlus, IconSettings, IconTrophyFilled } from "@tabler/icons-react";
import SettingsModal from "./SettingsModal";
import { paywall } from "./Authentication";
import { useLoggedOutStorage, useStorage } from "./ZustandState";
import { useNavigate } from "react-router-dom";
import { getCanWinTrophies } from "./utils";
import _ from "lodash";

interface Props {
    selectedPosition: Position;
    setSelectedPosition: (position: Position) => void;
    onStart: () => void;
    settings: Settings;
    user?: User;
    setMode: (mode: Mode) => void;
    mode: Mode;
    positionsToDisplay?: Position[];
}

const MenuPanel: React.FC<Props> = ({
    selectedPosition,
    setSelectedPosition,
    onStart,
    settings,
    user,
    setMode,
    mode,
    positionsToDisplay,
}) => {
    const { width } = useViewportSize();
    const levels = useStorage((state) => state.levels);
    const setLevels = useStorage((state) => state.setLevels);
    const playerColor = useStorage((state) => state.playerColor);
    const setPlayerColor = useStorage((state) => state.setPlayerColor);
    const navigate = useNavigate();
    const setIllegalMoveInMenu = useStorage((state) => state.setIllegalMoveInMenu);
    const loggedOutData = useLoggedOutStorage((state) => state);
    const setShowFirstGameModal = useStorage((state) => state.setShowFirstGameModal);
    const setOpenedSettings = useStorage((state) => state.setOpenedSettings);
    const openedSettings = useStorage((state) => state.openedSettings);

    const handleSetSelectedPosition = async (position: string) => {
        const startingPosition = positionsToDisplay?.find((pos) => pos.name === position) || {
            name: "Custom position",
            fen: position,
        };

        if (user && !user.isPremium && !startingPosition.standard && !startingPosition.isInitialPosition) {
            await paywall(user);
        }
        // setSelectedPosition(startingPosition);
        setSelectedPosition(startingPosition);
    };

    const handleOpenSettings = async () => {
        // await paywall(user);
        setOpenedSettings(true);
    };

    const isMobile = width < 1024;

    return (
        <div className="h-full relative flex flex-col justify-between max-lg:justify-normal max-w-[30rem] ml-auto mr-auto">
            {!!user && (user?.isPremium || !isMobile) && (
                <div className="absolute -top-4 -right-4">
                    <ActionIcon
                        onClick={openedSettings ? () => setOpenedSettings(false) : handleOpenSettings}
                        variant="transparent"
                        color="gray"
                    >
                        <IconSettings />
                    </ActionIcon>
                </div>
            )}
            <SettingsModal settings={settings} user={user} />

            <div className="space-y-10 mb-4 mt-0">
                <div>
                    <Text>Mode</Text>
                    <SegmentedControl
                        w="12rem"
                        className="mb-6 mt-0.5"
                        styles={{
                            root: {
                                backgroundColor: "#1c1d21",
                            },
                        }}
                        color={mode === "rated" ? "blue" : "blue"}
                        onChange={(mode) => {
                            setMode(mode as Mode);
                        }}
                        value={mode}
                        data={[
                            {
                                value: "rated",
                                disabled: !getCanWinTrophies(settings),
                                label: (
                                    <Center style={{ gap: 10 }}>
                                        <IconTrophyFilled style={{ width: rem(16), height: rem(16) }} />
                                        <span>Rated</span>
                                    </Center>
                                ),
                            },
                            { label: "Training", value: "practice" },
                        ]}
                    />
                    <div className="mb-8">
                        <div>Play as</div>
                        <SegmentedControl
                            w="12rem"
                            className="mt-0.5"
                            styles={{
                                root: {
                                    backgroundColor: "#1c1d21",
                                },
                            }}
                            color="blue"
                            value={playerColor}
                            onChange={(color) => setPlayerColor(color as PlayerColor)}
                            data={[
                                {
                                    value: "w",
                                    label: "White",
                                },
                                {
                                    value: "b",
                                    label: "Black",
                                },
                            ]}
                        />
                    </div>
                    <Select
                        label="Choose starting position"
                        placeholder="Pick value"
                        data={[...(positionsToDisplay?.map((pos) => pos.name) || []), "Add position"]}
                        renderOption={({ option }) => {
                            if (
                                !user?.isPremium &&
                                option.value !== "Initial position" &&
                                option.value !== "Add position"
                            ) {
                                return (
                                    <Group flex="1" gap="xs">
                                        <IconLock />
                                        {option.label}
                                    </Group>
                                );
                            }

                            if (option.value === "Add position") {
                                return (
                                    <div className="flex items-center space-x-1 font-bold">
                                        <IconPlus color="green" size="1.2rem" />
                                        <span>{option.label}</span>
                                    </div>
                                );
                            }

                            return option.label;
                        }}
                        value={selectedPosition.name}
                        allowDeselect={false}
                        onChange={async (option) => {
                            if (option === "Add position") {
                                const didRedirect = await paywall(user);
                                if (!didRedirect) {
                                    navigate(`/positions`);
                                }
                                return;
                            } else {
                                handleSetSelectedPosition(option!);
                            }
                        }}
                    />
                </div>

                {(mode === "practice" || !settings.isRated) && (
                    <div>
                        {!settings.allowMultipleLevels && (
                            <Select
                                label="Opponent level"
                                placeholder="Pick value"
                                data={[
                                    { label: "400-1000", value: "400" },
                                    { label: "1000-1200", value: "1000" },
                                    { label: "1200-1400", value: "1200" },
                                    { label: "1400-1600", value: "1400" },
                                    { label: "1600-1800", value: "1600" },
                                    { label: "1800-2000", value: "1800" },
                                    { label: "2000-2200", value: "2000" },
                                    { label: "2200-2500", value: "2200" },
                                    { label: "2500-2600", value: "2500" },
                                    { label: "Masters", value: "Masters" },
                                ]}
                                renderOption={({ option }) => {
                                    if (!user?.isPremium && ["2500", "Masters"].includes(option.value)) {
                                        return (
                                            <Group flex="1" gap="xs">
                                                <IconLock />
                                                {option.label}
                                            </Group>
                                        );
                                    }

                                    return option.label;
                                }}
                                value={levels[0]}
                                allowDeselect={false}
                                onChange={async (level) => {
                                    if (["2500", "Masters"].includes(level || "")) {
                                        await paywall(user);
                                    }
                                    setLevels([level as Level]);
                                }}
                            />
                        )}
                        {settings.allowMultipleLevels && (
                            <MultiSelect
                                label="Opponent level"
                                data={[
                                    { label: "400-1000", value: "400" },
                                    { label: "1000-1200", value: "1000" },
                                    { label: "1200-1400", value: "1200" },
                                    { label: "1400-1600", value: "1400" },
                                    { label: "1600-1800", value: "1600" },
                                    { label: "1800-2000", value: "1800" },
                                    { label: "2000-2200", value: "2000" },
                                    { label: "2200-2500", value: "2200" },
                                    { label: "2500-2600", value: "2500" },
                                    { label: "Masters", value: "Masters" },
                                ]}
                                renderOption={({ option }) => {
                                    return option.label;
                                }}
                                value={levels}
                                onChange={async (levels) => {
                                    // Alert user if both masters and other levels are selected
                                    if (levels.includes("Masters") && levels.length > 1) {
                                        alert("Masters level cannot be selected together with other levels");
                                        return;
                                    }
                                    setLevels(levels as Level[]);
                                }}
                            />
                        )}
                    </div>
                )}
                {mode === "rated" && (
                    <div>
                        <Text c="dimmed">Your rating</Text>
                        <Text fz="h2" mt="-0.4rem">
                            {selectedPosition.rating || 1200}
                        </Text>
                    </div>
                )}
            </div>

            <Button
                className={"!h-20 mb-6 max-md:my-22"}
                size="md"
                color="green"
                variant="filled"
                onClick={() => {
                    if (
                        !user &&
                        mode === "rated" &&
                        _.isEmpty(loggedOutData.whiteAttempts) &&
                        _.isEmpty(loggedOutData.blackAttempts)
                    ) {
                        setShowFirstGameModal(true);
                        return;
                    }

                    onStart();
                }}
                onAnimationEnd={() => setIllegalMoveInMenu(false)}
            >
                {mode === "practice" ? "Start training" : "Start rated"}
            </Button>
        </div>
    );
};

export default MenuPanel;
